import React, { useState, useEffect } from "react";
import apiClient from "../../../core/config/api";
import "../manage_users.css";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  selectedUserId: number | null;
  selectedUserName: string;
  onCreditsAssigned: () => void;
};

const AssignCreditsModal: React.FC<Props> = ({ isOpen, onClose, selectedUserId, selectedUserName, onCreditsAssigned }) => {
  const [step, setStep] = useState(2);
  const [creditsToAssign, setCreditsToAssign] = useState("");
  const [loading, setLoading] = useState(false);
  const [rootUserCredits, setRootUserCredits] = useState({ total_credits: 0, available_credits: 0 });
  const [selectedUserCurrentBalance, setSelectedUserCurrentBalance] = useState<string | number>("-");
  const [selectedUserUsedCredits, setSelectedUserUsedCredits] = useState<string | number>("-");
  const [selectedUserTotalAssigned, setSelectedUserTotalAssigned] = useState<string | number>("-");
  const [error, setError] = useState("");
  const [lastAssignedCredits, setLastAssignedCredits] = useState<number>(0);

  const formatNumber = (value?: number | null) => {
    if (value === undefined || value === null || Number.isNaN(value)) return "0";
    return value.toLocaleString("en-US");
  };

  const parsedCredits = creditsToAssign.replace(/,/g, "");
  const creditsNum = Number(parsedCredits) || 0;
  const availablePool = Number(rootUserCredits.available_credits || 0);
  const overLimit = creditsNum > availablePool;
  const isValid = creditsNum > 0;

  useEffect(() => {
    if (isOpen && selectedUserId) {
      apiClient.get('/account/re/credits/', { params: { _t: Date.now() } })
        .then((res: any) => {
          setRootUserCredits({
            total_credits: res.data.max_upload_limit || 0,
            available_credits: res.data.remaining_credits || 0,
          });
        })
        .catch(() => {});

      apiClient.get(`/re/sub-users/${selectedUserId}/credits`)
        .then((res: any) => {
          setSelectedUserCurrentBalance(res.data.remaining_credits || 0);
          setSelectedUserUsedCredits(res.data.used_credits || 0);
          setSelectedUserTotalAssigned(res.data.allocated_credits || 0);
        })
        .catch(() => {});
      setError("");
      setCreditsToAssign("");
      setLastAssignedCredits(0);
      setStep(2);
    }
  }, [isOpen, selectedUserId]);

  const handleAssign = async () => {
    if (!selectedUserId) return;
    if (overLimit) {
      setError(`Low balance: available ${availablePool.toLocaleString('en-US')}`);
      return;
    }
    try {
      setLoading(true);
      setError("");

      const creditResponse = await apiClient.get(`/re/sub-users/${selectedUserId}/credits`);
      const currentCredits = creditResponse.data.allocated_credits || 0;
      const newTotalCredits = currentCredits + creditsNum;

      await apiClient.put(`/re/sub-users/${selectedUserId}/credits`, {
        allocated_credits: newTotalCredits,
        bonus_credits: 0,
        payment_type: "one_time"
      });

      const updatedCreditResponse = await apiClient.get(`/re/sub-users/${selectedUserId}/credits`);
      setSelectedUserCurrentBalance(updatedCreditResponse.data.remaining_credits);
      setSelectedUserUsedCredits(updatedCreditResponse.data.used_credits);
      setSelectedUserTotalAssigned(updatedCreditResponse.data.allocated_credits);

      setLastAssignedCredits(creditsNum);
      setStep(2);
      onCreditsAssigned();
    } catch (err: any) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setStep(2);
    setCreditsToAssign("");
    setError("");
    onClose();
  };

  const handleBackToUserList = () => {
    setStep(2);
    setCreditsToAssign("");
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="popup-overlay" onClick={handleClose}>
      <div
        className="remove-popup-box"
        style={{
          backgroundImage: "url('/images/popup-back.png')",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "center",
          backgroundSize: "inherit",
          position: "relative",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={handleClose}
          aria-label="Close"
          style={{
            position: "absolute",
            top: 12,
            right: 12,
            background: "transparent",
            border: "none",
            fontSize: 20,
            cursor: "pointer",
            lineHeight: 1,
          }}
        >
          ×
        </button>
        {step === 1 && (
          <div className="d-flex flex-row justify-content-between">
            <p className="user-info">Assign Credits</p>
            <img
              src="/images/cancel.png"
              alt="close"
              style={{ cursor: "pointer" }}
              onClick={handleClose}
            />
          </div>
        )}

        {step === 1 && (
          <>
            <div className="mt-3">
              <p className="permanently-instantly">
                Enter the number of credits to assign. Changes will update instantly.
              </p>
            </div>

            <div className="cred-information">
              <div className="d-flex flex-row justify-content-center">
                <div className="ghor">
                  <p className="current-balance-c">User</p>
                  <p className="current-number">{selectedUserName}</p>
                </div>

                <div>
                  <img src="/images/d-line.png" className="line-images-c" height="60" alt="" />
                </div>

                <div className="ghor">
                  <p className="current-balance-c">Current Balance</p>
                  <p className="current-number">
                    {typeof selectedUserCurrentBalance === "number"
                      ? formatNumber(selectedUserCurrentBalance)
                      : selectedUserCurrentBalance}
                  </p>
                </div>
              </div>
            </div>

            <div className="d-flex flex-row justify-content-between">
              <div className="available d-flex flex-row">
                <div>
                  <p className="available-pool">Available Pool</p>
                </div>
                <div>
                  <p className="available-pool">:</p>
                </div>
                <div>
                  <p className="current-number-f">{formatNumber(rootUserCredits.available_credits)}</p>
                </div>
              </div>

              <div>
                <form className="password-form">
                  <div className="password-group">
                    <label className="password-label">Enter Credits to Assign</label>
                    <div className="password-input-wrapper">
                      <input
                        type="text"
                        inputMode="numeric"
                        className="creds-input"
                        placeholder="Enter number of credits"
                        value={creditsToAssign}
                        onChange={(e) => {
                          const digits = e.target.value.replace(/[^0-9]/g, "");
                          const formatted = digits ? Number(digits).toLocaleString("en-US") : "";
                          setCreditsToAssign(formatted);
                        }}
                      />
                    </div>
                  </div>
                </form>
                {overLimit && (
                  <div style={{ color: "#DF5D5D", fontSize: 12, marginTop: 4 }}>Low balance</div>
                )}
                {!overLimit && error && (
                  <div style={{ color: "#DF5D5D", fontSize: 12, marginTop: 4 }}>{error}</div>
                )}
              </div>
            </div>

            <div className="d-flex flex-row justify-content-between mt-2">
              <div>
                <button
                  type="button"
                  className="cancel"
                  onClick={handleClose}
                >
                  Cancel
                </button>
              </div>

              <div>
                <button
                  type="button"
                  className="save"
                  disabled={!isValid || loading || overLimit}
                  onClick={handleAssign}
                >
                  {loading ? "Assigning..." : "Assign Credits"}
                </button>
              </div>
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <div className="text-center">
              <img
                src="/images/shadow-check.png"
                className="invitation-img"
                alt=""
                style={{ marginBottom: 21.71 }}
              />

              <p className="invitation">
                {`${formatNumber(
                  typeof selectedUserTotalAssigned === "number"
                    ? selectedUserTotalAssigned
                    : Number(selectedUserTotalAssigned) || lastAssignedCredits || creditsNum
                )} credits have been successfully added to`}
                <br />
                {`${selectedUserName || "this user"}'s account.`}
              </p>

              <div className="cred-information" style={{ marginTop: 22 }}>
                <div className="d-flex flex-row justify-content-center">
                  <div className="ghor">
                    <p className="current-balance-c">Credits Remaining</p>
                    <p className="current-number">
                      {typeof selectedUserCurrentBalance === "number"
                        ? formatNumber(selectedUserCurrentBalance)
                        : selectedUserCurrentBalance}
                    </p>
                  </div>

                  <div>
                    <img src="/images/d-line.png" className="line-images-c" height="60" alt="" />
                  </div>

                  <div className="ghor">
                    <p className="current-balance-c">Credits Used</p>
                    <p className="current-number">
                      {typeof selectedUserUsedCredits === "number"
                        ? formatNumber(selectedUserUsedCredits)
                        : selectedUserUsedCredits}
                    </p>
                  </div>

                  <div>
                    <img src="/images/d-line.png" className="line-images-c" height="60" alt="" />
                  </div>

                  <div className="ghor">
                    <p className="current-balance-c">Total Assigned</p>
                    <p className="current-number">
                      {typeof selectedUserTotalAssigned === "number"
                        ? formatNumber(selectedUserTotalAssigned)
                        : selectedUserTotalAssigned}
                    </p>
                  </div>
                </div>
              </div>

              <div className="d-flex flex-row justify-content-between mt-2" style={{ gap: 16 }}>
                <button type="button" className="cancel" onClick={handleBackToUserList} style={{ flex: 1 }}>
                  Back to User List
                </button>

                <button
                  type="button"
                  className="save"
                  onClick={() => {
                    setStep(1);
                    setCreditsToAssign("");
                  }}
                  style={{ flex: 1 }}
                >
                  Add More Credits
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AssignCreditsModal;
